import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import * as arrow from "apache-arrow";
import { useTheme } from "@mui/material/styles";
import { TableVirtuoso, TableVirtuosoProps } from "react-virtuoso";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  getFormattedCell,
  getFormattedHeader,
  getFirstSegmentationColumnIndex,
  getFormattedTotalCell,
  formatHeaderLabel,
} from "../../utils/mui_table_utils";

export interface MUIFlatTableColumn {
  key: string;
  label?: string;
  sticky?: boolean;
  width?: number | string;
  align?: "left" | "center" | "right";
  renderHeader?: () => React.ReactNode;
  renderCell?: (row: any, rowIndex: number) => React.ReactNode;
  onSort?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  sortDirection?: "asc" | "desc" | undefined;
  sortable?: boolean;
  cellType?: arrow.DataType;
  headerType?: arrow.DataType;
  // Overrides col.key when passed to formatCellValue — used by pivot date
  // columns to carry the active measure name (e.g. "cost") so currency
  // formatting applies even though the column key is a date timestamp.
  currencyKey?: string;
  // Expand/collapse support
  isExpandColumn?: boolean;
  // Actuals/forecast boundary: dashed brand-blue right border on header and
  // body cells (set on the last actuals column by the chunked matrix hook).
  boundaryRight?: boolean;
  // Cell-level background tint (e.g. #F4F6F8 for actuals columns) applied by
  // the table shell — used on the generic pivot path where cell content
  // renderers don't paint their own backgrounds.
  bandTint?: string;
}

/**
 * ❯ ACTUALS / FORECAST strip config. Rendered as an extra header row above
 * the column headers, split at the run's as-of date. The pill/label are
 * position:sticky inside the horizontal scroll container.
 */
export interface MUIFlatTableHeaderBand {
  /** Sticky/label columns before the first date column (incl. expand col). */
  leadColumnCount: number;
  /** Date columns currently rendered in the actuals band (1 when collapsed). */
  actualsColumnCount: number;
  /** Date columns in the forecast band. */
  forecastColumnCount: number;
  /** Columns after the date columns (e.g. TOTAL). */
  trailingColumnCount: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Total px width of sticky lead columns — left offset for sticky labels. */
  stickyLeftWidth: number;
}

// Enhanced row interface to support expand/collapse (all properties optional for backward compatibility)
export interface MUIFlatTableRow {
  type?: "group" | "detail";
  groupKey?: string;
  isExpanded?: boolean;
  groupSize?: number;
  [key: string]: any;
}

export interface MUIFlatTableProps {
  columns: MUIFlatTableColumn[];
  getRow: (rowIndex: number) => MUIFlatTableRow;
  rowCount: number;
  stickyHeader?: boolean;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  sortConfig?: { key: string; direction: "asc" | "desc" }[];
  shadeHistory?: boolean;
  showHeaderRow?: boolean;
  columnTotals?:
    | Record<string, number>
    | Array<{ tableId: string; totals: Record<string, number> }>; // Support both structures
  // Expand/collapse support
  expandedGroups?: Set<string>;
  onToggleGroup?: (groupKey: string) => void;
  // Header expansion support
  isHeaderExpanded?: boolean;
  onToggleHeader?: () => void;
  // Actuals/forecast strip (see MUIFlatTableHeaderBand)
  headerBand?: MUIFlatTableHeaderBand;
}

/**
 * Effective column widths (and their sum), published by MUIFlatTable and
 * consumed by the module-level Table below. Context rather than a prop because
 * recreating the `components` object would remount the virtualized table on
 * every mousemove of a resize drag; context renders no DOM, so there's no
 * layout cost either.
 */
const TableWidthContext = React.createContext<{
  total: number;
  widths: number[];
} | null>(null);

const TableComponents: TableVirtuosoProps<any, unknown>["components"] = {
  Scroller: forwardRef<HTMLDivElement, any>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => {
    const layout = React.useContext(TableWidthContext);
    const { children, ...rest } = props;
    return (
    <Table
      {...rest}
      sx={{
        borderCollapse: "separate",
        // Fixed layout makes the declared/overridden column widths
        // authoritative. Under the default `auto` layout a column can never
        // render narrower than its widest cell's min-content, so dragging a
        // column below the length of (say) an unbroken SKU did nothing, and
        // the sticky-offset math — which sums declared widths — drifted
        // whenever auto layout rendered a column wider than declared.
        tableLayout: "fixed",
        // Fixed layout needs a definite table width. `100%` (fill the
        // container) combined with the colgroup below — real columns get exact
        // widths from the colgroup, and the trailing auto `<col>` filler
        // absorbs any leftover space. So the table fills the page when the
        // columns sum to less than the container, and scrolls when they
        // overflow, while real columns keep their exact widths either way
        // (measured: no stretch, sticky offsets stay correct). Earlier this was
        // the exact column sum, which left blank space on narrow tables.
        width: "100%",
      }}
    >
      {/*
        Under fixed layout the FIRST row decides column widths — and when the
        ❯ ACTUALS / FORECAST strip is on, that row is made of colSpan cells,
        which distributed every column evenly and ignored the declared widths
        entirely (measured: 320/222/278/120 all collapsed to 194). The sticky
        left-offsets are computed from the declared widths, so they then
        pointed at boundaries that didn't exist — blank gaps, and resize
        handles nowhere near the visible column edge. A colgroup outranks the
        first row, so widths hold regardless of what the header looks like.
      */}
      {layout && (
        <colgroup>
          {layout.widths.map((w, i) => (
            <col key={i} style={{ width: `${w}px` }} />
          ))}
          {/* Flex filler: no width, so fixed layout hands it the slack. Fills
              the page when columns are narrow; collapses to 0 (scroll) when
              they overflow. Paired with the empty <FillerCell/> appended to
              every row so the column actually exists. */}
          <col />
        </colgroup>
      )}
      {children}
    </Table>
    );
  },
  TableHead: forwardRef<HTMLTableSectionElement, any>((props, ref) => (
    <TableHead component="thead" ref={ref} {...props} />
  )),
  TableRow: TableRow,
  TableBody: forwardRef<HTMLTableSectionElement, any>((props, ref) => (
    <TableBody component="tbody" ref={ref} {...props} />
  )),
};

/**
 * Empty right-edge cell paired with the colgroup's flex filler <col>. Lets the
 * table fill the page without stretching (and thus misaligning) real columns.
 * `background` matches the row so the fill reads as an extension of that row.
 */
function FillerCell({
  background = "white",
  sx,
}: {
  background?: string;
  sx?: object;
}) {
  return (
    <TableCell
      aria-hidden
      sx={{ p: 0, border: 0, backgroundColor: background, ...sx }}
    />
  );
}

export const MUIFlatTable: React.FC<MUIFlatTableProps> = ({
  columns,
  getRow,
  rowCount,
  stickyHeader = true,
  columnHeaderMap,
  columnFormatters,
  sortConfig,
  shadeHistory = false,
  showHeaderRow = false,
  columnTotals,
  // Expand/collapse support
  expandedGroups,
  onToggleGroup,
  // Header expansion support
  isHeaderExpanded = false,
  onToggleHeader,
  headerBand,
}) => {
  const theme = useTheme();
  const atomicPalette = (theme.palette as any).atomic ?? {};
  const segmentRuleColor = atomicPalette.border ?? "#E6E8EC";
  const boundaryBorder = `2px dashed ${atomicPalette.brandBlue ?? "#3877F7"}`;

  // ── Resizable columns (in-session) ────────────────────────────────────────
  // Per-column width overrides keyed by col.key, set by dragging the handle on
  // the right edge of a header cell. Reset on reload. These survive sort
  // changes because only the inner TableVirtuoso remounts on sort (keyed
  // below), not this component, so the state persists.
  const MIN_COL_WIDTH = 40;
  const [widthOverrides, setWidthOverrides] = useState<Record<string, number>>(
    {}
  );
  // A resize drag that ends outside the 8px handle (dragging left, mostly)
  // dispatches its click on the header cell instead, which would sort. Set
  // while dragging and consumed by the header's onClick.
  const didResizeRef = useRef(false);
  // Under fixed layout an undeclared width would be handed an arbitrary share
  // of the table, so fall back to a concrete default. Columns that want
  // something else still declare their own width, and any of them can be
  // dragged to whatever the user actually wants.
  const DEFAULT_COL_WIDTH = 120;
  const getColWidth = useCallback(
    (col: MUIFlatTableColumn): number | string =>
      widthOverrides[col.key] ?? col.width ?? DEFAULT_COL_WIDTH,
    [widthOverrides]
  );

  // Cell contents clip to the column width rather than forcing it wider.
  // whiteSpace is inherited, so nested renderCell content stops wrapping too;
  // text sitting directly in the cell gets a real ellipsis.
  const TRUNCATE_SX = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as const;

  const resizingRef = useRef<{
    key: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const onResizeMove = useCallback((e: MouseEvent) => {
    const r = resizingRef.current;
    if (!r) return;
    didResizeRef.current = true;
    const next = Math.max(MIN_COL_WIDTH, r.startWidth + (e.clientX - r.startX));
    setWidthOverrides((prev) => ({ ...prev, [r.key]: next }));
  }, []);

  const onResizeEnd = useCallback(() => {
    resizingRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeEnd);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    // The click lands right after mouseup and before this timer, so the
    // header's onClick sees the flag and swallows exactly that one click.
    setTimeout(() => {
      didResizeRef.current = false;
    }, 0);
  }, [onResizeMove]);

  const startResize = useCallback(
    (e: React.MouseEvent, col: MUIFlatTableColumn) => {
      // Don't let the drag start a column sort or text selection.
      e.preventDefault();
      e.stopPropagation();
      const th = (e.currentTarget as HTMLElement).closest("th");
      const measured = th?.getBoundingClientRect().width ?? 150;
      const current = widthOverrides[col.key] ?? col.width;
      const startWidth =
        typeof current === "number" && !isNaN(current) ? current : measured;
      resizingRef.current = { key: col.key, startX: e.clientX, startWidth };
      window.addEventListener("mousemove", onResizeMove);
      window.addEventListener("mouseup", onResizeEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widthOverrides, onResizeMove, onResizeEnd]
  );

  // Drop listeners if we unmount mid-drag.
  useEffect(
    () => () => {
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup", onResizeEnd);
    },
    [onResizeMove, onResizeEnd]
  );

  // Effective width of every column, plus their sum. Emitted as a colgroup by
  // the Table component so the widths hold no matter what the first header row
  // looks like. Memoized on the values so a re-render that doesn't change any
  // width doesn't restyle the table.
  const tableLayout = React.useMemo(() => {
    const widths = columns.map((c) => {
      const n = Number(widthOverrides[c.key] ?? c.width);
      return isNaN(n) ? DEFAULT_COL_WIDTH : n;
    });
    return { widths, total: widths.reduce((a, b) => a + b, 0) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, widthOverrides]);

  const firstSegmentationIndex = getFirstSegmentationColumnIndex(
    columns,
    getRow,
    rowCount
  );

  // Calculate left offset for sticky columns
  const getStickyLeftOffset = (columnIndex: number) => {
    let offset = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (columns[i].sticky) {
        offset += Number(getColWidth(columns[i])) || 0;
      }
    }
    return offset;
  };

  return (
    <TableWidthContext.Provider value={tableLayout}>
    <TableVirtuoso
      key={sortConfig ? JSON.stringify(sortConfig) : "no-sort"}
      totalCount={rowCount}
      components={TableComponents}
      fixedHeaderContent={() => (
        <>
          {/* ❯ ACTUALS / FORECAST strip */}
          {headerBand && headerBand.actualsColumnCount > 0 && (
            <TableRow>
              <TableCell
                colSpan={headerBand.leadColumnCount}
                sx={{
                  p: 0,
                  height: "26px",
                  backgroundColor: "white",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  borderBottom: "none",
                }}
              />
              <TableCell
                colSpan={headerBand.actualsColumnCount}
                sx={{
                  p: 0,
                  height: "26px",
                  backgroundColor: "#F4F6F8",
                  borderRight: boundaryBorder,
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={headerBand.onToggleCollapsed}
                  aria-label={
                    headerBand.collapsed
                      ? "Expand actuals columns"
                      : "Collapse actuals columns"
                  }
                  sx={{
                    position: "sticky",
                    left: headerBand.stickyLeftWidth + 8,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    margin: "3px 0",
                    padding: "1px 8px",
                    border: "none",
                    borderRadius: "9px",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    backgroundColor: atomicPalette.brandBlue ?? "#3877F7",
                    color: "#fff",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      transition: "transform 120ms ease",
                      transform: headerBand.collapsed
                        ? "rotate(0deg)"
                        : "rotate(90deg)",
                    }}
                  >
                    ❯
                  </Box>
                  ACTUALS
                </Box>
              </TableCell>
              {headerBand.forecastColumnCount +
                headerBand.trailingColumnCount >
                0 && (
                <TableCell
                  colSpan={
                    headerBand.forecastColumnCount +
                    headerBand.trailingColumnCount
                  }
                  sx={{
                    p: 0,
                    height: "26px",
                    backgroundColor: "white",
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {headerBand.forecastColumnCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        position: "sticky",
                        left: headerBand.stickyLeftWidth + 8,
                        display: "inline-block",
                        margin: "3px 0 3px 10px",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        color: atomicPalette.muted ?? "#8A8F98",
                      }}
                    >
                      FORECAST
                    </Box>
                  )}
                </TableCell>
              )}
              <FillerCell />
            </TableRow>
          )}

          {/* Column headers */}
          <TableRow>
            {columns.map((col, index) => {
              const isSortable = col.sortable && col.onSort;
              // For sortable columns, always use getFormattedHeader to show sort arrows
              // For non-sortable columns, use label if available, otherwise use getFormattedHeader
              const headerContent = col.isExpandColumn
                ? "" // Empty string for expand columns
                : col.renderHeader
                  ? col.renderHeader()
                  : isSortable
                    ? getFormattedHeader(
                        columnHeaderMap ?? {},
                        col.headerType ?? new arrow.Utf8(),
                        sortConfig?.find((s) => s.key === col.key)
                          ? {
                              ...sortConfig.find((s) => s.key === col.key)!,
                              index: sortConfig.findIndex(
                                (s) => s.key === col.key
                              ),
                            }
                          : undefined,
                        col.key
                      )
                    : col.label ||
                      getFormattedHeader(
                        columnHeaderMap ?? {},
                        col.headerType ?? new arrow.Utf8(),
                        sortConfig?.find((s) => s.key === col.key)
                          ? {
                              ...sortConfig.find((s) => s.key === col.key)!,
                              index: sortConfig.findIndex(
                                (s) => s.key === col.key
                              ),
                            }
                          : undefined,
                        col.key
                      );
              // Plain-text column name for the truncation tooltip. Skipped
              // for expand or custom-rendered headers where there's no name.
              const rawLabel =
                col.isExpandColumn || col.renderHeader
                  ? null
                  : (col.label ??
                    formatHeaderLabel(
                      columnHeaderMap ?? {},
                      col.headerType ?? new arrow.Utf8(),
                      col.key
                    ));
              const headerLabelText =
                typeof rawLabel === "string" ? rawLabel : "";
              // Every column is resizable, including the expand column.
              const canResize = true;
              const tooltipTitle =
                headerLabelText || isSortable ? (
                  <Box>
                    {headerLabelText && (
                      <Box sx={{ fontWeight: 600 }}>{headerLabelText}</Box>
                    )}
                    {isSortable && (
                      <Box sx={{ fontSize: 11, opacity: 0.85 }}>
                        Click to sort · Shift+Click for multi-sort
                      </Box>
                    )}
                  </Box>
                ) : (
                  ""
                );
              const justifyContent =
                col.align === "right"
                  ? "flex-end"
                  : col.align === "center"
                    ? "center"
                    : "flex-start";
              return (
                <Tooltip
                  key={col.key}
                  title={tooltipTitle}
                  placement="bottom-start"
                  disableHoverListener={!tooltipTitle}
                  disableFocusListener={!tooltipTitle}
                  disableTouchListener={!tooltipTitle}
                  slotProps={{
                    tooltip: {
                      sx: {
                        marginTop: "-4px !important",
                        transform: "translateY(-4px) !important",
                      },
                    },
                  }}
                >
                  <TableCell
                    align={col.align || "left"}
                    sx={{
                      position: "relative",
                      backgroundColor: "white",
                      cursor: isSortable ? "pointer" : "default",
                      height: "48px", // Slightly taller for headers
                      verticalAlign: "middle",
                      fontWeight: "bold",
                      width: getColWidth(col),
                      minWidth: getColWidth(col),
                      ...(col.boundaryRight && {
                        borderRight: boundaryBorder,
                      }),
                      ...(col.sticky && {
                        position: "sticky",
                        top: 0,
                        left: getStickyLeftOffset(index),
                        backgroundColor: "white",
                        zIndex: 3,
                      }),
                    }}
                    onClick={
                      isSortable
                        ? (e: React.MouseEvent<HTMLTableCellElement>) => {
                            if (didResizeRef.current) return;
                            col.onSort?.(e);
                          }
                        : undefined
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent,
                        minWidth: 0,
                        // Reserve space so the label never sits under the
                        // resize handle.
                        pr: canResize ? "8px" : 0,
                      }}
                    >
                      <Box
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {headerContent}
                      </Box>
                    </Box>
                    {canResize && (
                      <Box
                        onMouseDown={(e) => startResize(e, col)}
                        onClick={(e) => e.stopPropagation()}
                        // Above the neighbouring sticky header cells, which
                        // share zIndex 3 and would otherwise paint over this
                        // strip at the shared column boundary.
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          height: "100%",
                          width: "12px",
                          cursor: "col-resize",
                          userSelect: "none",
                          zIndex: 6,
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "stretch",
                          // Invisible until hovered, then a clear grab rail so
                          // the target is discoverable rather than guessed at.
                          "&:hover::after": {
                            content: '""',
                            width: "3px",
                            backgroundColor:
                              atomicPalette.brandBlue ?? "#3877F7",
                          },
                        }}
                      />
                    )}
                  </TableCell>
                </Tooltip>
              );
            })}
            <FillerCell />
          </TableRow>

          {/* Summary row with totals */}
          {showHeaderRow &&
            (() => {
              const hasTableId = columns.some((col) => col.key === "table_id");
              const hasExpandColumn = columns.some((col) => col.isExpandColumn);

              // Helper function to render header cells
              const renderHeaderCell = (
                col: MUIFlatTableColumn,
                index: number,
                rowData: Record<string, any>,
                backgroundColor: string,
                fontWeight: string = "normal",
                showExpandButton: boolean = false
              ) => {
                if (col.isExpandColumn) {
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{
                        p: 0,
                        height: "40px",
                        verticalAlign: "middle",
                        width: getColWidth(col),
                        minWidth: getColWidth(col),
                        backgroundColor,
                        ...(col.sticky && {
                          position: "sticky",
                          backgroundColor,
                          left: getStickyLeftOffset(index),
                          zIndex: 1,
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0,
                        }}
                      >
                        {showExpandButton && onToggleHeader && (
                          <IconButton
                            size="small"
                            onClick={onToggleHeader}
                            sx={{ p: 0 }}
                          >
                            {isHeaderExpanded ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  );
                }

                const cellContent = col.renderCell
                  ? col.renderCell(rowData, -1)
                  : getFormattedCell({
                      value: rowData[col.key],
                      col: col.currencyKey ?? col.key,
                      columnFormatters,
                      row: rowData,
                      isDelta: rowData.table_id === "delta (1st - 2nd)",
                      theme,
                      columnType: col.cellType,
                      headerType: col.headerType,
                      shadeHistory,
                      align: col.align,
                    });

                return (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      p: 0,
                      height: "40px",
                      verticalAlign: "middle",
                      width: getColWidth(col),
                      minWidth: getColWidth(col),
                      backgroundColor,
                      fontWeight,
                      ...TRUNCATE_SX,
                      ...(col.sticky && {
                        position: "sticky",
                        backgroundColor,
                        left: getStickyLeftOffset(index),
                        zIndex: 1,
                      }),
                    }}
                  >
                    {cellContent}
                  </TableCell>
                );
              };

              // For comparison tables with expand/collapse, create expandable header rows
              if (hasTableId && hasExpandColumn) {
                const deltaTableId = "delta (1st - 2nd)";
                const deltaHeaderRow: Record<string, any> = {
                  type: "group" as const,
                  groupKey: `header-${deltaTableId}`,
                  isExpanded: false,
                  groupSize: 2,
                  table_id: deltaTableId,
                  channel: "Total",
                  "product category": "Total",
                  ...(Array.isArray(columnTotals) &&
                    columnTotals.find((t) => t.tableId === deltaTableId)
                      ?.totals),
                };

                return (
                  <>
                    {/* Main delta header row */}
                    <TableRow
                      key={`header-${deltaTableId}`}
                      sx={{
                        backgroundColor: theme.palette.grey[50],
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      {columns.map((col, index) =>
                        renderHeaderCell(
                          col,
                          index,
                          deltaHeaderRow,
                          theme.palette.grey[50],
                          "bold",
                          true
                        )
                      )}
                      <FillerCell background={theme.palette.grey[50]} />
                    </TableRow>

                    {/* Expanded 1st and 2nd rows */}
                    {isHeaderExpanded &&
                      ["1st", "2nd"].map((tableId) => {
                        const childHeaderRow: Record<string, any> = {
                          type: "detail" as const,
                          groupKey: `header-${deltaTableId}`,
                          table_id: tableId,
                          channel: "Total",
                          "product category": "Total",
                          ...(Array.isArray(columnTotals) &&
                            columnTotals.find((t) => t.tableId === tableId)
                              ?.totals),
                        };

                        return (
                          <TableRow
                            key={`header-child-${tableId}`}
                            sx={{
                              backgroundColor: theme.palette.grey[100],
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            {columns.map((col, index) =>
                              renderHeaderCell(
                                col,
                                index,
                                childHeaderRow,
                                theme.palette.grey[100]
                              )
                            )}
                            <FillerCell background={theme.palette.grey[100]} />
                          </TableRow>
                        );
                      })}
                  </>
                );
              }

              // For other tables, use the original logic
              const tableIds = hasTableId
                ? ["1st", "2nd", "delta (1st - 2nd)"]
                : [undefined];

              return tableIds.map((tableId) => (
                <TableRow
                  key={tableId ? `total-${tableId}` : "total"}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                  }}
                >
                  {columns.map((col, index) =>
                    getFormattedTotalCell({
                      col,
                      index,
                      firstSegmentationIndex,
                      columnTotals,
                      getRow,
                      rowCount,
                      theme,
                      tableId,
                      isComparison: hasTableId,
                      isSticky: col.sticky,
                      stickyLeftOffset: col.sticky
                        ? getStickyLeftOffset(index)
                        : 0,
                    })
                  )}
                  <FillerCell background={theme.palette.grey[50]} />
                </TableRow>
              ));
            })()}
        </>
      )}
      itemContent={(rowIndex: number) => {
        const row = getRow(rowIndex);
        if (row?.__chunkedLoading) {
          return (
            <>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ color: "text.secondary" }}>
                  Loading…
                </TableCell>
              ))}
              <FillerCell />
            </>
          );
        }
        const isGroup = row.type === "group";

        // Helper function to render expand/collapse button
        const renderExpandButton = (row: MUIFlatTableRow) => {
          if (
            !isGroup ||
            !row.groupSize ||
            row.groupSize <= 1 ||
            !onToggleGroup
          ) {
            return null;
          }

          return (
            <IconButton
              size="small"
              onClick={() => onToggleGroup(row.groupKey!)}
              sx={{ p: 0 }}
            >
              {row.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          );
        };

        // Render detail rows as a single full-width cell in the __details column
        const isDetail = row.type === "detail";
        const detailsColumnIndex = columns.findIndex(
          (c) => c.key === "__details"
        );
        if (isDetail && detailsColumnIndex !== -1) {
          return (
            <>
              {columns.map((col, index) => {
                if (col.isExpandColumn) {
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{
                        p: 0,
                        height: "40px",
                        verticalAlign: "middle",
                        width: getColWidth(col),
                        minWidth: getColWidth(col),
                        backgroundColor: "white",
                        ...(col.sticky && {
                          position: "sticky",
                          backgroundColor: "white",
                          left: getStickyLeftOffset(index),
                          zIndex: 1,
                        }),
                      }}
                    />
                  );
                }

                if (col.key === "__details") {
                  const cellContent = col.renderCell
                    ? col.renderCell(row, rowIndex)
                    : null;
                  return (
                    <TableCell
                      key={col.key}
                      colSpan={columns.length - 1}
                      sx={{ p: 0 }}
                    >
                      {cellContent}
                    </TableCell>
                  );
                }
                return null;
              })}
              <FillerCell />
            </>
          );
        }

        return (
          <>
            {columns.map((col, index) => {
              // Handle expand column
              if (col.isExpandColumn) {
                return (
                  <TableCell
                    key={col.key}
                    align="center"
                    sx={{
                      p: 0,
                      // Group rows are taller with a 2px top rule so every
                      // group (SKU) reads as a distinct block.
                      height: isGroup ? "44px" : "40px",
                      ...(isGroup && {
                        borderTop: `2px solid ${segmentRuleColor}`,
                      }),
                      verticalAlign: "middle",
                      width: getColWidth(col),
                      minWidth: getColWidth(col),
                      backgroundColor: "white", // Removed row styling props
                      ...(col.sticky && {
                        position: "sticky",
                        backgroundColor: "white",
                        left: getStickyLeftOffset(index),
                        zIndex: 1,
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                      }}
                    >
                      {renderExpandButton(row)}
                    </Box>
                  </TableCell>
                );
              }

              // Use renderCell if provided, otherwise use default formatting
              const cellContent = col.renderCell
                ? col.renderCell(row, rowIndex)
                : getFormattedCell({
                    value: row[col.key],
                    col: col.currencyKey ?? col.key,
                    columnFormatters,
                    row,
                    isDelta: row.table_id === "delta (1st - 2nd)",
                    theme,
                    columnType: col.cellType,
                    headerType: col.headerType,
                    shadeHistory,
                    align: col.align,
                  });

              return (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{
                    p: 0, // Always no padding - let the content handle its own padding
                    // Group rows are taller with a 2px top rule so every
                    // group (SKU) reads as a distinct block.
                    height: isGroup ? "44px" : "40px",
                    ...(isGroup && {
                      borderTop: `2px solid ${segmentRuleColor}`,
                    }),
                    verticalAlign: "middle",
                    width: getColWidth(col),
                    minWidth: getColWidth(col),
                    ...TRUNCATE_SX,
                    ...(col.bandTint && {
                      backgroundColor: col.bandTint,
                    }),
                    ...(col.boundaryRight && {
                      borderRight: boundaryBorder,
                    }),
                    ...(col.sticky && {
                      position: "sticky",
                      backgroundColor: "white",
                      left: getStickyLeftOffset(index),
                      zIndex: 1,
                    }),
                  }}
                >
                  {cellContent}
                </TableCell>
              );
            })}
            <FillerCell
              sx={
                isGroup
                  ? { borderTop: `2px solid ${segmentRuleColor}` }
                  : undefined
              }
            />
          </>
        );
      }}
    />
    </TableWidthContext.Provider>
  );
};
